import React, { useState } from "react";
import { format, isToday, isBefore, isPast } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { EnhancedLead } from "@/hooks/use-dashboard-data";
import { LeadStatusBadge } from "./leads/LeadStatusBadge";
import { LeadStatusSelect } from "./leads/LeadStatusSelect";
import {
  Building2,
  MapPin,
  Calendar,
  Bell,
  BellOff,
  StickyNote,
  ExternalLink,
  Trash2,
  ChevronDown,
  ChevronUp,
  Plus,
  CheckCircle2,
  Clock,
  AlertCircle,
  PlusCircle,
  Crown,
  ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/* ── Status pipeline ── */
const STATUS_STEPS = [
  { value: "interested", label: "Interested" },
  { value: "contacted", label: "Contacted" },
  { value: "visit_scheduled", label: "Visit Booked" },
  { value: "visited", label: "Visited" },
  { value: "shortlisted", label: "Shortlisted" },
  { value: "application_submitted", label: "Applied" },
  { value: "waitlist", label: "On Waitlist" },
];

function ProgressPipeline({ status }: { status: string }) {
  const currentIdx = STATUS_STEPS.findIndex((s) => s.value === status);
  const idx = currentIdx === -1 ? 0 : currentIdx;

  return (
    <div className="flex items-center gap-0.5 overflow-x-auto py-1">
      {STATUS_STEPS.map((step, i) => (
        <React.Fragment key={step.value}>
          <div
            className={cn(
              "flex-shrink-0 text-[10px] font-medium px-2 py-0.5 rounded-full transition-colors whitespace-nowrap",
              i < idx
                ? "bg-primary/20 text-primary"
                : i === idx
                ? "bg-primary text-primary-foreground"
                : "bg-muted/60 text-muted-foreground"
            )}
          >
            {i < idx && <CheckCircle2 size={9} className="inline mr-0.5" />}
            {step.label}
          </div>
          {i < STATUS_STEPS.length - 1 && (
            <div
              className={cn(
                "h-px flex-1 min-w-[8px] transition-colors",
                i < idx ? "bg-primary/40" : "bg-muted"
              )}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

/* ── Premium nudge banner ── */
function PremiumNudgeBanner({ message }: { message: string }) {
  return (
    <Link
      to="/premium"
      className="flex items-center gap-3 px-4 py-3 rounded-lg border border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50 hover:border-amber-300 transition-colors group"
    >
      <div className="flex-shrink-0 flex items-center justify-center h-8 w-8 rounded-full bg-amber-100">
        <Crown className="h-4 w-4 text-amber-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-amber-900">{message}</p>
        <p className="text-xs text-amber-700 mt-0.5">Upgrade to Premium — €12.99/month</p>
      </div>
      <ArrowRight className="h-4 w-4 text-amber-500 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
    </Link>
  );
}

/* ── Single enquiry card ── */
interface EnquiryCardProps {
  lead: EnhancedLead & { follow_up_date?: string | null };
  onUpdate: () => void;
  isPremium?: boolean;
}

function EnquiryCard({ lead, onUpdate, isPremium = false }: EnquiryCardProps) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState(lead.notes || "");
  const [savingNotes, setSavingNotes] = useState(false);
  const [followUpValue, setFollowUpValue] = useState(
    lead.follow_up_date || ""
  );
  const [savingFollowUp, setSavingFollowUp] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  /* ── helpers ── */
  const followUpDate = lead.follow_up_date
    ? new Date(lead.follow_up_date)
    : null;
  const followUpOverdue =
    followUpDate && isPast(followUpDate) && !isToday(followUpDate);
  const followUpToday = followUpDate && isToday(followUpDate);

  const saveNotes = async () => {
    setSavingNotes(true);
    const { error } = await supabase
      .from("nursing_home_leads")
      .update({ notes: notesValue })
      .eq("id", lead.id);
    setSavingNotes(false);
    if (error) {
      toast.error("Failed to save notes");
    } else {
      toast.success("Notes saved");
      setEditingNotes(false);
      onUpdate();
    }
  };

  const saveFollowUp = async (value: string) => {
    setSavingFollowUp(true);
    const { error } = await supabase
      .from("nursing_home_leads")
      .update({ follow_up_date: value || null } as any)
      .eq("id", lead.id);
    setSavingFollowUp(false);
    if (error) {
      toast.error("Failed to set reminder");
    } else {
      toast.success(value ? "Reminder set" : "Reminder cleared");
      onUpdate();
    }
  };

  const handleStatusChange = async (id: string, newStatus: string) => {
    setUpdatingStatus(id);
    const { error } = await supabase
      .from("nursing_home_leads")
      .update({ status: newStatus })
      .eq("id", id);
    setUpdatingStatus(null);
    if (error) {
      toast.error("Failed to update status");
    } else {
      toast.success("Status updated");
      onUpdate();
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Remove ${lead.nursing_home_name} from your tracker?`)) return;
    setDeleting(true);
    const { error } = await supabase
      .from("nursing_home_leads")
      .delete()
      .eq("id", lead.id);
    setDeleting(false);
    if (error) {
      toast.error("Failed to remove");
    } else {
      toast.success("Removed from tracker");
      onUpdate();
    }
  };

  /* ── render ── */
  const countySlug = lead.location?.toLowerCase().replace(/\s+/g, "-") || "";
  const profileUrl = `/nursing-homes/${countySlug}/${lead.nursing_home_id}`;

  return (
    <Card
      className={cn(
        "border transition-shadow",
        followUpOverdue
          ? "border-destructive/40 shadow-sm"
          : followUpToday
          ? "border-yellow-400/60 shadow-sm"
          : "border-border"
      )}
    >
      <CardContent className="p-0">
        {/* Header row */}
        <div className="flex items-start gap-3 p-4">
          {/* Icon */}
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
            <Building2 size={18} className="text-primary" />
          </div>

          {/* Main info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <button
                  onClick={() => navigate(profileUrl)}
                  className="font-semibold text-foreground hover:text-primary transition-colors text-left truncate block max-w-full"
                >
                  {lead.nursing_home_name}
                </button>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                  {lead.location && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <MapPin size={11} />
                      {lead.location}
                    </span>
                  )}
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Calendar size={11} />
                    Enquired {format(new Date(lead.created_at), "d MMM yyyy")}
                  </span>
                  {followUpDate && (
                    <span
                      className={cn(
                        "flex items-center gap-1 text-xs font-medium",
                        followUpOverdue
                          ? "text-destructive"
                          : followUpToday
                          ? "text-yellow-600"
                          : "text-muted-foreground"
                      )}
                    >
                      {followUpOverdue ? (
                        <AlertCircle size={11} />
                      ) : followUpToday ? (
                        <Bell size={11} />
                      ) : (
                        <Clock size={11} />
                      )}
                      {followUpOverdue
                        ? "Overdue – "
                        : followUpToday
                        ? "Today – "
                        : "Follow-up "}
                      {format(followUpDate, "d MMM")}
                    </span>
                  )}
                </div>
              </div>

              {/* Status + expand */}
              <div className="flex items-center gap-2 shrink-0">
                <LeadStatusBadge status={lead.status || "interested"} />
                <button
                  onClick={() => setExpanded((v) => !v)}
                  className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded"
                  aria-label={expanded ? "Collapse" : "Expand"}
                >
                  {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
              </div>
            </div>

            {/* Progress pipeline (always visible) */}
            <div className="mt-3">
              <ProgressPipeline status={lead.status || "interested"} />
            </div>
          </div>
        </div>

        {/* Expanded panel */}
        {expanded && (
          <>
            <Separator />
            <div className="p-4 space-y-5">
              {/* Change status */}
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-muted-foreground w-24 shrink-0">
                  Status
                </span>
                <LeadStatusSelect
                  leadId={lead.id}
                  status={lead.status || "interested"}
                  updatingStatus={updatingStatus}
                  onStatusChange={handleStatusChange}
                />
              </div>

              {/* Notes */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium flex items-center gap-1.5">
                    <StickyNote size={14} className="text-muted-foreground" />
                    Notes
                  </span>
                  {!editingNotes && (
                    lead.notes || isPremium ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 text-xs"
                        onClick={() => setEditingNotes(true)}
                      >
                        {lead.notes ? "Edit" : "Add note"}
                      </Button>
                    ) : (
                      <Link
                        to="/premium"
                        className="inline-flex items-center gap-1 text-xs font-medium text-amber-600 hover:text-amber-700 transition-colors"
                        title="Upgrade to Premium to add notes"
                      >
                        <Crown size={11} />
                        Add note — Premium
                      </Link>
                    )
                  )}
                </div>
                {editingNotes ? (
                  <div className="space-y-2">
                    <Textarea
                      value={notesValue}
                      onChange={(e) => setNotesValue(e.target.value)}
                      placeholder="e.g. Spoke with admissions on 3rd Feb. Waiting list 2–3 months. Request brochure."
                      rows={3}
                      className="text-sm resize-none"
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={saveNotes}
                        disabled={savingNotes}
                        className="h-7 text-xs"
                      >
                        {savingNotes ? "Saving…" : "Save"}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs"
                        onClick={() => {
                          setNotesValue(lead.notes || "");
                          setEditingNotes(false);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : lead.notes ? (
                  <p className="text-sm text-foreground bg-muted/40 rounded-md px-3 py-2 whitespace-pre-wrap">
                    {lead.notes}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground italic">
                    No notes yet. Add reminders, contacts, or anything relevant.
                  </p>
                )}
              </div>

              {/* Follow-up reminder */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium flex items-center gap-1.5">
                    <Bell size={14} className="text-muted-foreground" />
                    Follow-up reminder
                  </span>
                  {lead.follow_up_date && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-xs text-muted-foreground"
                      disabled={savingFollowUp}
                      onClick={() => {
                        setFollowUpValue("");
                        saveFollowUp("");
                      }}
                    >
                      <BellOff size={11} className="mr-1" />
                      Clear
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={followUpValue}
                    onChange={(e) => setFollowUpValue(e.target.value)}
                    className="h-8 text-sm w-44"
                    min={format(new Date(), "yyyy-MM-dd")}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 text-xs"
                    disabled={savingFollowUp || followUpValue === (lead.follow_up_date || "")}
                    onClick={() => saveFollowUp(followUpValue)}
                  >
                    {savingFollowUp ? "Saving…" : "Set reminder"}
                  </Button>
                </div>
              </div>

              {/* Footer actions */}
              <div className="flex items-center justify-between pt-1">
                <div className="flex items-center gap-2">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs gap-1"
                          onClick={() => navigate(profileUrl)}
                        >
                          <ExternalLink size={11} />
                          View profile
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Open nursing home profile</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  <Trash2 size={11} className="mr-1" />
                  {deleting ? "Removing…" : "Remove"}
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/* ── Main tracker component ── */
interface EnquiryTrackerProps {
  leads: Array<EnhancedLead & { follow_up_date?: string | null }>;
  onLeadUpdate: () => void;
  isPremium?: boolean;
}

const STATUS_FILTERS = [
  { label: "All", value: null },
  { label: "Interested", value: "interested" },
  { label: "Contacted", value: "contacted" },
  { label: "Visit Booked", value: "visit_scheduled" },
  { label: "Visited", value: "visited" },
  { label: "Shortlisted", value: "shortlisted" },
  { label: "Applied", value: "application_submitted" },
  { label: "Waitlist", value: "waitlist" },
];

export function EnquiryTracker({ leads, onLeadUpdate, isPremium = false }: EnquiryTrackerProps) {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [showRemindersOnly, setShowRemindersOnly] = useState(false);

  const dueToday = leads.filter(
    (l) => l.follow_up_date && isToday(new Date(l.follow_up_date))
  ).length;
  const overdue = leads.filter(
    (l) =>
      l.follow_up_date &&
      isPast(new Date(l.follow_up_date)) &&
      !isToday(new Date(l.follow_up_date))
  ).length;

  const shortlistedCount = leads.filter((l) => l.status === "shortlisted").length;

  const filtered = leads.filter((l) => {
    if (statusFilter && l.status !== statusFilter) return false;
    if (showRemindersOnly && !l.follow_up_date) return false;
    return true;
  });

  if (leads.length === 0) {
    return (
      <div className="text-center py-16">
        <Building2 className="mx-auto h-12 w-12 text-muted-foreground/40 mb-4" />
        <h3 className="text-lg font-semibold mb-2">No enquiries yet</h3>
        <p className="text-muted-foreground text-sm mb-6 max-w-xs mx-auto">
          When you contact a nursing home, it'll appear here so you can track your progress.
        </p>
        <Button onClick={() => navigate("/nursing-homes")} className="gap-2">
          <PlusCircle size={16} />
          Browse nursing homes
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Summary row */}
      {(dueToday > 0 || overdue > 0) && (
        <div className="flex flex-wrap gap-2">
          {dueToday > 0 && (
            <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300 gap-1.5 py-1 px-3 text-xs font-medium">
              <Bell size={12} />
              {dueToday} follow-up{dueToday > 1 ? "s" : ""} due today
            </Badge>
          )}
          {overdue > 0 && (
            <Badge variant="destructive" className="gap-1.5 py-1 px-3 text-xs font-medium">
              <AlertCircle size={12} />
              {overdue} overdue
            </Badge>
          )}
        </div>
      )}

      {/* Premium nudge — shortlisted > 3 */}
      {!isPremium && shortlistedCount > 3 && (
        <PremiumNudgeBanner message={`You've shortlisted ${shortlistedCount} homes — Premium lets you manage them all with notes, reminders & managed enquiries.`} />
      )}

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1.5 flex-1">
          {STATUS_FILTERS.map((f) => (
            <button
              key={String(f.value)}
              onClick={() => setStatusFilter(f.value)}
              className={cn(
                "px-3 py-1 rounded-full text-xs font-medium transition-colors",
                statusFilter === f.value
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              )}
            >
              {f.label}
              {f.value === null && (
                <span className="ml-1 opacity-60">({leads.length})</span>
              )}
            </button>
          ))}
        </div>

        <button
          onClick={() => setShowRemindersOnly((v) => !v)}
          className={cn(
            "flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors shrink-0",
            showRemindersOnly
              ? "bg-amber-500 text-white"
              : "bg-muted text-muted-foreground hover:bg-muted/80"
          )}
        >
          <Bell size={11} />
          Reminders only
        </button>

        <Button
          size="sm"
          variant="outline"
          className="shrink-0 h-7 text-xs gap-1"
          onClick={() => navigate("/nursing-homes")}
        >
          <Plus size={12} />
          Add home
        </Button>
      </div>

      {/* Cards */}
      {filtered.length === 0 ? (
        <p className="text-center text-muted-foreground py-10 text-sm">
          No homes match your current filter.
        </p>
      ) : (
        <div className="space-y-3">
          {filtered.map((lead) => (
            <EnquiryCard key={lead.id} lead={lead} onUpdate={onLeadUpdate} isPremium={isPremium} />
          ))}
        </div>
      )}
    </div>
  );
}
